import { z } from "zod";

// Helper to coerce common truthy strings to boolean
const toBoolean = (value: unknown, defaultValue = false): boolean => {
  if (typeof value !== "string") return defaultValue;
  const v = value.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
};

// Define the base Vite env schema for variables we rely on
const RawEnvSchema = z.object({
  VITE_GRAPHQL_URL: z.string().min(1),
  VITE_GRAPHQL_WS_URL: z.string().min(1),
  VITE_API_URL: z.string().optional(),
  VITE_APP_NAME: z.string().optional(),
  VITE_APP_VERSION: z.string().optional(),
  VITE_NODE_ENV: z.string().optional(),
  VITE_FIREBASE_VAPID_KEY: z.string().optional(),
  VITE_STOREFRONT_URL: z.string().optional(),
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_UPDATE_URL: z.string().optional(),

  // Experimental features flag (true = show experimental features, false = hide them)
  VITE_EXPERIMENTAL: z.string().optional(),

  // Demo seed selection (standard, german-demo, or both)
  VITE_DEMO_SEED: z.string().optional(),
});

const rawEnv = RawEnvSchema.parse(import.meta.env);

const experimental = toBoolean(rawEnv.VITE_EXPERIMENTAL, false);
const apiUrl = rawEnv.VITE_API_URL ? rawEnv.VITE_API_URL : new URL(rawEnv.VITE_GRAPHQL_URL).origin;

export const env = {
  // Build info
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  // Core URLs
  graphqlUrl: rawEnv.VITE_GRAPHQL_URL,
  graphqlWsUrl: rawEnv.VITE_GRAPHQL_WS_URL,
  apiUrl,

  // Storefront URL (Next.js app). Defaults to local dev port used by the app.
  storefrontUrl: rawEnv.VITE_STOREFRONT_URL ?? "http://localhost:3230",

  // Sentry DSN for error tracking
  sentryDsn: rawEnv.VITE_SENTRY_DSN,

  // Update URL for Capacitor Updater
  updateUrl:
    rawEnv.VITE_UPDATE_URL ??
    "https://96beauty-app.fra1.digitaloceanspaces.com/salon-updates/version.json",

  // Metadata
  appName: rawEnv.VITE_APP_NAME ?? "Salon Management Dashboard",
  appVersion: rawEnv.VITE_APP_VERSION ?? "0.0.0",

  // Experimental features flag (true = show experimental features, false = hide them)
  experimental,

  // Demo seed configuration (standard, german-demo, or both)
  demoSeed: rawEnv.VITE_DEMO_SEED,

  // NODE_ENV is "production" on staging too; only the API host tells prod apart.
  isProdDeploy: new URL(rawEnv.VITE_GRAPHQL_URL).host === "api.beauty.96digital.de",

  // Simplified feature flag system
  flags: {
    showExperimental: experimental,
  },
} as const;

export type Env = typeof env;
